/*
 * Super Admin — Service Inquiries inbox (Phase 7).
 * Mounted at /app/service-inquiries.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { LifeBuoy, RefreshCcw, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const adminHdrs = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id":   session?.user?.id   || "",
  "x-user-role": session?.user?.role || "admin",
});

const STATUS_PILL = {
  new:           "text-[#1e40af] bg-[#eff6ff] border-[#bfdbfe]",
  contacted:     "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  proposal_sent: "text-[#00607d] bg-[#f0f9fc] border-[#bfe3ee]",
  closed_won:    "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  closed_lost:   "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]",
};

const STATUSES = ["all", "new", "contacted", "proposal_sent", "closed_won", "closed_lost"];

const CATEGORY_LABELS = {
  annual_report_readiness: "Annual Report Readiness",
  board_reporting: "Board Reporting",
  compliance_improvement: "Compliance Improvement",
  iep_special_ed_process: "IEP / Special Ed Process",
  governance: "Governance",
  cmo_emo_evaluation: "CMO/EMO Evaluation",
  instructional_walkthrough: "Instructional Walkthrough",
  corrective_action_implementation: "Corrective Action Implementation",
  ai_workflow_optimization: "AI Workflow Optimization",
  custom: "Custom",
};

const ServiceInquiriesAdminPage = ({ session }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/service-inquiries`, { headers: adminHdrs(session) });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => statusFilter === "all" || r.status === statusFilter);
  }, [rows, statusFilter]);

  const counts = useMemo(() => {
    const out = { all: rows.length };
    STATUSES.slice(1).forEach((s) => { out[s] = rows.filter((r) => r.status === s).length; });
    return out;
  }, [rows]);

  const update = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/admin/service-inquiries/${id}`, patch, { headers: adminHdrs(session) });
      setRows((arr) => arr.map((x) => (x.id === id ? r.data : x)));
      if (selected?.id === id) setSelected(r.data);
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto" data-testid="service-inquiries-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Operations</div>
          <h1 className="text-3xl font-bold text-[#111827] flex items-center gap-2">
            <LifeBuoy className="h-7 w-7 text-[#007a9e]" /> Service Inquiries
          </h1>
          <p className="text-sm text-[#6b7280] mt-1">Customer requests for follow-on services. Triage, contact, and convert.</p>
        </div>
        <Button onClick={load} variant="outline" className="border-[#e5e7eb]"><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
              statusFilter === s ? "bg-[#0b1220] text-white border-[#0b1220]" : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
            }`}
            data-testid={`inquiry-status-${s}`}
          >
            {s === "all" ? "All" : s.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())} <span className={`ml-1 text-[11.5px] ${statusFilter === s ? "text-white/60" : "text-[#9ca3af]"}`}>({counts[s] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#6b7280]">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
            <table className="w-full text-[13px]" data-testid="service-inquiries-table">
              <thead className="bg-[#f9fafb] text-[11.5px] uppercase text-[#6b7280]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">School</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Category</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-[#9ca3af]">No inquiries yet.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className={`cursor-pointer border-t border-[#e5e7eb] ${selected?.id === r.id ? "bg-[#eff6ff]" : "hover:bg-[#f9fafb]"}`}
                    data-testid={`inquiry-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#111827]">{r.tenant_name}</div>
                      <div className="text-[11.5px] text-[#9ca3af]">{r.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#374151]">{CATEGORY_LABELS[r.inquiry_type] || r.inquiry_type}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded border ${STATUS_PILL[r.status]}`}>{r.status.replace("_", " ")}</span>
                    </td>
                    <td className="px-4 py-3 text-[#6b7280]">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="lg:sticky lg:top-6 lg:self-start">
            {!selected ? (
              <div className="rounded-xl border border-[#e5e7eb] bg-white p-10 text-center text-[#9ca3af]">Select an inquiry to view details.</div>
            ) : (
              <div className="rounded-xl border border-[#e5e7eb] bg-white p-5 space-y-3" data-testid="inquiry-detail-panel">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">{CATEGORY_LABELS[selected.inquiry_type] || selected.inquiry_type}</div>
                    <h2 className="text-lg font-bold text-[#111827]">{selected.tenant_name}</h2>
                    <div className="text-[11.5px] text-[#6b7280] mt-0.5">{selected.user_email} · {new Date(selected.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-[#9ca3af] hover:text-[#111827]"><X className="h-4 w-4" /></button>
                </div>

                <div className="text-[12px] text-[#374151] space-y-1">
                  {selected.pillar && <div><strong>Pillar:</strong> {selected.pillar}</div>}
                  {selected.risk_area && <div><strong>Risk area:</strong> {selected.risk_area}</div>}
                  {selected.diagnostic_id && <div><strong>Diagnostic:</strong> {selected.diagnostic_id}</div>}
                </div>

                <div className="rounded-md border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-[13px] text-[#374151] whitespace-pre-wrap">
                  {selected.message}
                </div>

                <label className="block text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold mt-2">Status</label>
                <select
                  value={selected.status}
                  onChange={(e) => update(selected.id, { status: e.target.value })}
                  className="border border-[#e5e7eb] bg-white text-[#111827] text-[13px] rounded-md px-2 py-2 w-full"
                  data-testid="inquiry-status-select"
                >
                  {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>)}
                </select>

                <label className="block text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold mt-2">Internal notes</label>
                <Textarea
                  defaultValue={selected.notes}
                  onBlur={(e) => e.target.value !== selected.notes && update(selected.id, { notes: e.target.value })}
                  placeholder="Add a note…"
                  className="bg-white border-[#e5e7eb] text-[13px] min-h-[80px]"
                  data-testid="inquiry-notes"
                />
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default ServiceInquiriesAdminPage;
